import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { ChevronLeft, Calendar, Loader, Target, ChevronRight, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;
const spin = keyframes`100%{transform:rotate(360deg)}`;

const Page = styled.div`
  display:flex;flex-direction:column;height:calc(100vh - 64px);
  background:#080a12;color:#fff;font-family:'Inter',sans-serif;
`;
const PageHeader = styled.div`
  padding:16px 20px;border-bottom:1px solid #16182a;background:#0c0e1a;
  display:flex;align-items:center;justify-content:space-between;
`;
const BackBtn = styled(Link)`
  color:#888;display:flex;align-items:center;text-decoration:none;font-size:14px;
  &:hover{color:#67e8f9}
`;
const Title = styled.h2`
  font-size:18px;margin:0;display:flex;align-items:center;gap:8px;
  .badge{font-size:10px;background:linear-gradient(135deg,#22d3ee,#0891b2);padding:2px 8px;border-radius:10px;font-weight:700;}
`;
const Layout = styled.div`
  display:flex;flex:1;overflow:hidden;
  @media(max-width:768px){flex-direction:column;overflow-y:auto}
`;
const Sidebar = styled.div`
  width:300px;border-right:1px solid #16182a;display:flex;flex-direction:column;
  background:#0a0c18;
  @media(max-width:768px) {
    width:100%; border-right:none; display: ${p => p.$hiddenOnMobile ? 'none' : 'flex'};
  }
`;
const ListHeader = styled.div`
  padding:12px 16px;font-size:12px;color:#4b5563;border-bottom:1px solid #16182a;
  text-transform:uppercase;letter-spacing:.5px;
`;
const SnapshotList = styled.div`flex:1;overflow-y:auto`;
const SnapshotItem = styled.div`
  padding:14px 16px;border-bottom:1px solid #10121f;cursor:pointer;
  background:${p=>p.$active?'rgba(34,211,238,.12)':'transparent'};
  border-left:3px solid ${p=>p.$active?'#22d3ee':'transparent'};
  &:hover{background:${p=>p.$active?'rgba(34,211,238,.12)':'rgba(34,211,238,.04)'}}
`;
const SnapTitle = styled.div`
  font-weight:600;font-size:13px;margin-bottom:3px;
  color:${p=>p.$active?'#67e8f9':'#ccc'};
`;
const SnapMeta = styled.div`font-size:11px;color:#4b5563;display:flex;gap:10px`;
const Detail = styled.div`
  flex:1;overflow-y:auto;padding:24px;
  @media(max-width:768px) {
    padding:16px; display: ${p => p.$visibleOnMobile ? 'block' : 'none'};
  }
`;
const DetailTitle = styled.div`
  font-size:22px;font-weight:700;color:#67e8f9;margin-bottom:4px;
`;
const SectionTitle = styled.h3`
  font-size:13px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.7px;
  margin:24px 0 14px;display:flex;align-items:center;gap:8px;
`;
const PivotCard = styled.div`
  display:flex;justify-content:space-between;align-items:center;
  padding:14px 16px;border-radius:10px;margin-bottom:8px;
  background:rgba(34,211,238,.04);border:1px solid rgba(34,211,238,.1);
  cursor:pointer;transition:all .18s;animation:${fadeIn} .3s;
  &:hover{background:rgba(34,211,238,.1);border-color:rgba(34,211,238,.3)}
`;
const StatusBadge = styled.span`
  font-size:10px;font-weight:700;padding:2px 6px;border-radius:4px;margin-left:6px;
  background:${p=>p.s==='BREAKOUT'?'rgba(239,68,68,.15)':p.s==='NEAR_BREAKOUT'?'rgba(234,179,8,.12)':'rgba(99,102,241,.1)'};
  color:${p=>p.s==='BREAKOUT'?'#ef4444':p.s==='NEAR_BREAKOUT'?'#eab308':'#818cf8'};
`;
const PerformanceBadge = styled.div`
  display:flex;flex-direction:column;align-items:flex-end;
  background:${p=>p.$neutral?'rgba(255,255,255,.03)':'rgba(239,68,68,.1)'};
  padding:4px 8px;border-radius:6px;
  border:1px solid ${p=>p.$neutral?'#16182a':'rgba(239,68,68,.2)'};
`;
const EmptyMsg = styled.div`
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  height:100%;color:#374151;gap:12px;font-size:14px;padding:40px;text-align:center;
  @media(max-width:768px) { display: ${p => p.$hideOnMobile ? 'none' : 'flex'}; }
`;
const Spinner = styled(Loader)`animation:${spin} 1s linear infinite;margin-bottom:16px;`;
const SyncingOverlay = styled.div`
  position:fixed;top:0;left:0;width:100%;height:100%;
  background:rgba(0,0,0,0.85);display:flex;flex-direction:column;
  align-items:center;justify-content:center;z-index:9999;backdrop-filter:blur(4px);
`;

export default function LivermoreV31HistoryPage() {
    const [snapshots, setSnapshots] = useState([]);
    const [selectedSnap, setSelectedSnap] = useState(null);
    const [details, setDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        apiService.getLivermoreV31History().then(data => {
            setSnapshots(data || []);
            if (data && data.length > 0 && window.innerWidth > 768) {
                handleSelect(data[0]);
            }
        }).catch(console.error).finally(() => setLoading(false));
    }, []);

    const handleSelect = async (snap) => {
        setSelectedSnap(snap);
        setDetailsLoading(true);
        setDetails(null);
        try {
            const data = await apiService.getLivermoreV31HistoryDetail(snap.id);
            setDetails(data);

            const needsCalc = data.pivotPoints?.some(
                p => !p.performance || p.performance.todayMax === '-' || p.performance.nextDayMax === '-'
            );
            if (data.pivotPoints?.length > 0 && needsCalc) {
                const createdAt = new Date(data.createdAt);
                const nextDay = new Date(createdAt);
                nextDay.setDate(nextDay.getDate() + 1);
                nextDay.setHours(15, 30, 0, 0);
                if (new Date() > nextDay) {
                    const updated = await apiService.calculateLivermoreV31Performance(snap.id);
                    if (updated.success) {
                        setDetails(prev => ({ ...prev, pivotPoints: updated.pivotPoints }));
                    }
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleRecalculate = async () => {
        if (!selectedSnap) return;
        const confirmed = window.confirm('현재 성과를 다시 계산하시겠습니까?');
        if (!confirmed) return;

        setIsSyncing(true);
        setSyncMessage('성과 재계산 중...');
        await new Promise(resolve => setTimeout(resolve, 50));
        try {
            const updated = await apiService.calculateLivermoreV31Performance(selectedSnap.id, true);
            if (updated.success) {
                setDetails(prev => ({ ...prev, pivotPoints: updated.pivotPoints }));
                alert('성과 재계산이 완료되었습니다.');
            }
        } catch (e) {
            console.error(e);
            alert('재계산 중 오류가 발생했습니다.');
        } finally {
            setIsSyncing(false);
        }
    };

    const formatTime = (iso) => {
        if (!iso) return '';
        return new Date(iso).toLocaleString('ko-KR', {
            month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: false
        });
    };

    return (
        <Page>
            {isSyncing && (
                <SyncingOverlay>
                    <Spinner size={48} color="#22d3ee" />
                    <h2 style={{ margin: 0, marginBottom: '8px', color: '#67e8f9' }}>{syncMessage || '분석 중...'}</h2>
                </SyncingOverlay>
            )}
            <PageHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {isMobile && selectedSnap ? (
                        <button
                            onClick={() => setSelectedSnap(null)}
                            style={{ background: 'none', border: 'none', color: '#67e8f9', display: 'flex', alignItems: 'center', padding: 0 }}
                        >
                            <ChevronLeft size={20} /> 리스트
                        </button>
                    ) : (
                        <BackBtn to="/labs/livermore-v31"><ChevronLeft size={16} />뒤로</BackBtn>
                    )}
                </div>
                <Title>
                    <Calendar size={18} color="#22d3ee"/>
                    Livermore V3.1 히스토리
                    <span className="badge">V3.1</span>
                </Title>
                <button
                    onClick={handleRecalculate}
                    disabled={isSyncing || !selectedSnap}
                    style={{
                        background: selectedSnap ? 'rgba(34, 211, 238, 0.2)' : '#16182a',
                        color: selectedSnap ? '#67e8f9' : '#4b5563',
                        border: `1px solid ${selectedSnap ? '#22d3ee' : '#16182a'}`,
                        padding: '6px 14px', borderRadius: '8px', fontSize: '12px',
                        fontWeight: 700, cursor: (isSyncing || !selectedSnap) ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    {isSyncing ? <Spinner size={12} style={{ margin: 0 }} /> : <Zap size={12} />}
                    {isSyncing ? '계산 중...' : '다시 분석하기'}
                </button>
            </PageHeader>

            <Layout>
                <Sidebar $hiddenOnMobile={isMobile && selectedSnap}>
                    <ListHeader>RECORDED SNAPSHOTS</ListHeader>
                    <SnapshotList>
                        {loading && <EmptyMsg><Loader size={20} color="#22d3ee"/>로딩 중...</EmptyMsg>}
                        {!loading && snapshots.length === 0 && (
                            <EmptyMsg>저장된 기록이 없습니다</EmptyMsg>
                        )}
                        {snapshots.map(snap => (
                            <SnapshotItem
                                key={snap.id}
                                $active={selectedSnap?.id === snap.id}
                                onClick={() => handleSelect(snap)}
                            >
                                <SnapTitle $active={selectedSnap?.id === snap.id}>
                                    {formatTime(snap.createdAt)} Analysis
                                </SnapTitle>
                                <SnapMeta>
                                    <span style={{color:snap.marketMood==='HOT'?'#ef4444':'#6b7280'}}>
                                        {snap.marketMood}
                                    </span>
                                    <span>{snap.leaderCount}L | {snap.pivotCount}P</span>
                                </SnapMeta>
                            </SnapshotItem>
                        ))}
                    </SnapshotList>
                </Sidebar>

                {!selectedSnap ? (
                    <EmptyMsg style={{flex:1}} $hideOnMobile={isMobile}>
                        <Calendar size={40} color="#16182a"/>
                        <span>히스토리를 선택하세요</span>
                    </EmptyMsg>
                ) : (
                    <Detail $visibleOnMobile={!isMobile || selectedSnap}>
                        <DetailTitle>{formatTime(details?.createdAt || selectedSnap.createdAt)}</DetailTitle>
                        <div style={{fontSize:'13px',color:'#4b5563',marginBottom:8}}>
                            Market Mood: <strong style={{color:details?.marketMood==='HOT'?'#ef4444':'#67e8f9'}}>
                                {details?.marketMood || selectedSnap.marketMood}
                            </strong>
                        </div>

                        {detailsLoading && <EmptyMsg><Loader size={20} color="#22d3ee"/>분석 로딩 중...</EmptyMsg>}

                        {details && !detailsLoading && (
                            <>
                                <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:8}}>
                                    {['VCP 필터 적용','시총/52W 폴백 제거','대칭 뉴스 패널티','우선주 차단','40일 Base + 정배열'].map(tag=>(
                                        <span key={tag} style={{
                                            fontSize:'10px',padding:'2px 8px',borderRadius:4,
                                            background:'rgba(34,211,238,.1)',color:'#67e8f9',
                                            border:'1px solid rgba(34,211,238,.2)'
                                        }}>{tag}</span>
                                    ))}
                                </div>

                                <SectionTitle>
                                    <Target size={14} color="#22d3ee"/>
                                    Identified Pivot Points ({details.pivotPoints?.length || 0})
                                </SectionTitle>
                                {(details.pivotPoints || []).map((item, i) => (
                                    <PivotCard key={item.code} style={{animationDelay:`${i*30}ms`}} onClick={() => window.open(`/labs/stock/technical?symbol=${item.code}${item.resistanceLine ? `&pivot=${item.resistanceLine}` : ''}`, '_blank')}>
                                        <div>
                                            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                                                <span style={{fontWeight:700,color:'#e2e8f0'}}>{item.name}</span>
                                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>{item.price?.toLocaleString()}원</span>
                                                <StatusBadge s={item.pivotStatus}>{item.pivotStatus}</StatusBadge>
                                                {item.isVolumeDried && <span style={{fontSize:'10px', marginLeft:'6px',color:'#34d399'}}>VCP</span>}
                                                {item.isUptrend && <span style={{fontSize:'10px', marginLeft:'6px',color:'#34d399'}}>정배열</span>}
                                                {item.isTrendStarter && (
                                                    <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>
                                                        👑장기추세 초입
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{fontSize:'11px',color:'#374151'}}>
                                                Vol Spike: {item.volSurge?.toFixed(1)}x · Volatility: {item.volatility}
                                                {item.targetUpside && item.pivotStatus !== 'REJECTED' && <span style={{marginLeft: 8, color: '#10b981', fontWeight: 600}}>🎯상승여력: {item.targetUpside}</span>}
                                                {item.rejectReason && <span style={{marginLeft: 8, color: '#ef4444'}}>· 탈락: {item.rejectReason}</span>}
                                            </div>
                                        </div>
                                        <div style={{display:'flex',alignItems:'center',gap:12}}>
                                            {item.performance && (
                                                <>
                                                     {[['TODAY', item.performance.todayMax],['NEXT DAY', item.performance.nextDayMax]].map(([label, val]) => {
                                                         const isNeutral = val === '-';
                                                         const isPositive = val.includes('+');
                                                         const color = isNeutral ? '#374151' : (isPositive ? '#ef4444' : '#3b82f6');
                                                         return (
                                                             <PerformanceBadge key={label} $neutral={isNeutral} style={{
                                                                 background: isNeutral ? 'rgba(255,255,255,.03)' : (isPositive ? 'rgba(239,68,68,.05)' : 'rgba(59, 130, 246,.05)'),
                                                                 borderColor: isNeutral ? '#16182a' : (isPositive ? 'rgba(239,68,68,0.2)' : 'rgba(59, 130, 246,0.2)'),
                                                             }}>
                                                                 <span style={{fontSize:'9px',color:'#4b5563',textTransform:'uppercase'}}>{label} MAX</span>
                                                                 <span style={{fontSize:'13px',fontWeight:700,color}}>{val}</span>
                                                             </PerformanceBadge>
                                                         );
                                                     })}
                                                </>
                                            )}
                                            <ChevronRight size={16} color="#16182a"/>
                                        </div>
                                    </PivotCard>
                                ))}

                                {/* Top Leaders Table */}
                                {details.leaders && details.leaders.length > 0 && (
                                    <>
                                        <SectionTitle>
                                            <Zap size={14} color="#f59e0b" />
                                            Top Leading Stocks Scanner
                                        </SectionTitle>
                                        <div style={{ background: 'rgba(255,255,255,.02)', borderRadius: 12, border: '1px solid rgba(255,255,255,.05)', padding: 16 }}>
                                            {details.leaders.slice(0, 15).map((l, idx) => (
                                                <div key={l.code} onClick={() => window.open(`/labs/stock/technical?symbol=${l.code}`, '_blank')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.05)', cursor: 'pointer' }}>
                                                    <div>
                                                        <span style={{ fontSize: '12px', color: '#64748b', marginRight: 8 }}>#{idx + 1}</span>
                                                        <span style={{ fontWeight: 600, color: '#e2e8f0', marginRight: 8, fontSize: '13px' }}>{l.name}</span>
                                                        <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: l.grade === 'S' ? 'rgba(239,68,68,.15)' : l.grade === 'A' ? 'rgba(234,179,8,.12)' : 'rgba(99,102,241,.1)', color: l.grade === 'S' ? '#ef4444' : l.grade === 'A' ? '#eab308' : '#818cf8', fontWeight: 700 }}>{l.grade}</span>
                                                        {l.isTrendStarter && (
                                                            <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold', marginLeft: '6px' }}>
                                                                👑장기추세
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ textAlign: 'right', fontSize: '12px' }}>
                                                        <div style={{ color: l.changeRate >= 0 ? '#ef4444' : '#3b82f6', fontWeight: 600 }}>
                                                            {l.changeRate >= 0 ? '+' : ''}{l.changeRate?.toFixed(2)}%
                                                        </div>
                                                        <div style={{ color: '#475569', fontSize: '11px' }}>
                                                            52W: <span style={{ color: '#22d3ee' }}>{l.highProximity}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </Detail>
                )}
            </Layout>
        </Page>
    );
}
